import React, { useState } from 'react';
import { MapPin, ChevronLeft, ChevronRight } from 'lucide-react';

const HOMEOWNER_PROJECTS = [
  {
    id: 'project-1',
    title: 'Whole-Home Water Treatment',
    location: 'Massapequa, NY',
    description: "This Massapequa homeowner was looking for a better way to reduce unwanted contaminants in their water. After evaluating the home's water quality, we recommended a custom filtration solution designed to fit their specific needs. The result was cleaner, higher-quality water and greater peace of mind every day.",
    image: `${process.env.PUBLIC_URL}/contaminant-reduction-massapequa-ny.png`,
    alt: 'Black contaminant reduction system installed in a Massapequa, New York home',
  },
  {
    id: 'project-3',
    title: 'Whole-Home Water Treatment',
    location: 'Port Washington, NY',
    description: "Aquify installed a custom whole-home water conditioning and contaminant removal system designed to reduce unwanted contaminants and address hard water throughout the home. The completed system provides cleaner, better-tasting water while helping protect plumbing fixtures and household appliances from mineral buildup. After installation, the system was tested to ensure reliable long-term performance.",
    image: `${process.env.PUBLIC_URL}/port-washington-water-system.jpg`,
    alt: 'Custom combination water conditioning and contaminant removal system in Port Washington, NY',
  },
  {
    id: 'project-4',
    title: 'Well Water Treatment System',
    location: 'Sagaponack, NY',
    description: "Aquify installed a custom treatment system designed specifically for the home's private well water. The solution improves water clarity, taste, and overall quality while providing dependable whole-home filtration. Every system is professionally installed, tested, and optimized to deliver reliable performance and cleaner water throughout the home.",
    image: `${process.env.PUBLIC_URL}/sagaponack-well-water-system.jpg`,
    alt: 'Custom well water treatment system installed in Sagaponack, NY',
  },
  {
    id: 'project-5',
    title: 'Reverse Osmosis System',
    location: 'South Jamesport, NY',
    description: "Aquify installed an under-sink reverse osmosis system to provide cleaner, better-tasting drinking water directly from the kitchen tap. The system was professionally integrated, thoroughly tested, and configured for reliable everyday use. The finished installation gives the homeowner convenient access to purified drinking water while reducing many common contaminants.",
    image: `${process.env.PUBLIC_URL}/south-jamesport-ro-system.jpg`,
    alt: 'Reverse osmosis drinking water system installed in South Jamesport, NY',
    objectPosition: '25% center',
  },
  {
    id: 'project-2',
    title: 'Whole-Home Water Treatment',
    location: 'Atlantic Beach, NY',
    description: "When this Atlantic Beach homeowner wanted greater confidence in their drinking water, we helped them find a solution tailored to their home's needs. After evaluating their water, we installed a custom contaminant reduction system designed to improve water quality and provide cleaner, better-tasting water every day. It's a great example of how understanding your water leads to the right solution.",
    image: `${process.env.PUBLIC_URL}/contaminant-reduction-atlantic-beach-ny.jpg`,
    alt: 'Custom contaminant reduction system installed in Atlantic Beach, New York',
  },
  {
    id: 'project-6',
    title: 'Whole Home Conditioning & Filtration System',
    location: 'Delray Beach, FL',
    description: "Every home has different water, which is why we never recommend a one-size-fits-all solution. For this Delray Beach homeowner, we installed a custom whole-home conditioning and filtration system to improve water quality throughout the entire house. The result is cleaner, better-quality water from every tap and a system designed specifically for their home's needs.",
    image: `${process.env.PUBLIC_URL}/whole-home-filtration-delray-beach-fl.jpg`,
    alt: 'Whole-home water conditioning and filtration system installed in Delray Beach, Florida',
  }
];

export const SolutionsSection = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const total = HOMEOWNER_PROJECTS.length;

  const prev = () => setActiveIdx((i) => (i - 1 + total) % total);
  const next = () => setActiveIdx((i) => (i + 1) % total);

  // Keyboard navigation support
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') prev();
    if (e.key === 'ArrowRight') next();
  };

  const project = HOMEOWNER_PROJECTS[activeIdx];

  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Homeowner Project Highlights
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real problems solved. Real results delivered. Explore Aquify project stories from homeowners across Long Island.
          </p>
        </div>

        {/* ── Carousel ── */}
        <div className="relative max-w-4xl mx-auto group">
          <div className="relative">
            {/* Card */}
            <div
              className="rounded-3xl border-2 border-gray-200 overflow-hidden shadow-xl transition-all duration-300 bg-white flex flex-col md:flex-row md:h-[450px]"
              tabIndex="0"
              onKeyDown={handleKeyDown}
              aria-label="Homeowner Project Carousel"
            >
              {/* Project Image Column */}
              <div className="relative w-full md:w-[45%] lg:w-[42%] h-[360px] md:h-full bg-slate-100 flex-shrink-0 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.alt}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ objectPosition: project.objectPosition || 'center' }}
                />
              </div>

              {/* Story content Column */}
              <div className="px-6 py-8 md:px-8 lg:px-10 md:py-10 flex-1 flex flex-col">
              <div className="flex items-center text-[#1571BA] font-semibold bg-[#F0F7FF] px-4 py-2 rounded-full whitespace-nowrap self-start mb-4">
                <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                {project.location}
              </div>
              
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 leading-tight mb-4">
                {project.title}
              </h3>
                
                <div className="w-16 h-1 bg-gradient-to-r from-[#1571BA] to-[#50BAED] mb-6 rounded-full flex-shrink-0"></div>
                
                <p className="text-slate-600 text-lg leading-relaxed">
                  {project.description}
                </p>
              </div>
            </div>

            {/* Navigation arrows */}
            <button
              onClick={prev}
              aria-label="Previous project"
              className="absolute z-30 left-0 top-[180px] md:top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-6 w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110 focus:ring-2 focus:ring-[#1571BA] focus:outline-none"
              style={{ backgroundColor: '#ffffff', border: '2px solid #E5E7EB', color: '#0C4288' }}
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={next}
              aria-label="Next project"
              className="absolute z-30 right-0 top-[180px] md:top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-6 w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110 focus:ring-2 focus:ring-[#1571BA] focus:outline-none"
              style={{ backgroundColor: '#ffffff', border: '2px solid #E5E7EB', color: '#0C4288' }}
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-3 mt-8">
            {HOMEOWNER_PROJECTS.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIdx(i)}
                aria-label={`Go to project ${i + 1}`}
                className="w-2.5 h-2.5 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1571BA]"
                style={{
                  backgroundColor: i === activeIdx ? '#1571BA' : '#CBD5E1',
                  transform: i === activeIdx ? 'scale(1.3)' : 'scale(1)',
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionsSection;
